import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Link } from 'components/_new/Link';
import { usePanel } from 'contexts/panelContext';
import { Spinner } from 'components/_new/Spinner';
import { Pair } from 'types/Pair';
import { useAuth } from 'contexts/authContext';
import { useMutation } from '@tanstack/react-query';
import { usersApi } from 'api';
import Gratz from 'assets/popup-gratz.png';
import { NumberCircle } from './NumberCircle';

type WelcomeDialogPairContentProps = {
	pair: Pair;
};

export const WelcomeDialogPairContent = ({ pair }: WelcomeDialogPairContentProps) => {
	const { t } = useTranslation();
	const { panelScopePath } = usePanel();
	return (
		<>
			<li>
				<div className="flex flex-row gap-2 mt-2 align-items-center">
					<NumberCircle number={1} />
					<p>
						<Trans
							t={t}
							i18nKey="userPanel.welcomeDialog.pairContent.firstElement"
							components={[
								<Link
									to={{
										pathname: `${panelScopePath}/pair/${pair.id}/pair-details`,
									}}
									target="_blank"
								/>,
							]}
						/>
						<Link
							to={`${panelScopePath}/pair/${pair.id}/pair-details`}
							className="text-purplish-blue text-center ml-2"
							target="_blank"
						>
							<i className="fa-solid fa-arrow-up-right-from-square" />
						</Link>
					</p>
				</div>
			</li>
			<li>
				<div className="flex flex-row gap-2 mt-2 align-items-center">
					<NumberCircle number={2} />
					<p>
						<Trans
							t={t}
							i18nKey="userPanel.welcomeDialog.pairContent.secondElement"
							components={[
								<Link
									to={{
										pathname: `${panelScopePath}/pair/${pair.id}/pair-details`,
									}}
									target="_blank"
								/>,
							]}
						/>
						<Link
							to={`${panelScopePath}/pair/${pair.id}/pair-details`}
							className="text-purplish-blue text-center ml-2"
							target="_blank"
						>
							<i className="fa-solid fa-arrow-up-right-from-square" />
						</Link>
					</p>
				</div>
			</li>
			<li>
				<div className="flex flex-row gap-2 mt-2 align-items-center">
					<NumberCircle number={3} />
					<p>
						<Trans
							t={t}
							i18nKey="userPanel.welcomeDialog.pairContent.thirdElement"
							components={[
								<Link
									to={{ pathname: `${panelScopePath}/knowledge-base/directory/3` }}
									target="_blank"
								/>,
								<Link
									to={{ pathname: `${panelScopePath}/knowledge-base/article/60` }}
									target="_blank"
								/>,
							]}
						/>
						<Link
							to={`${panelScopePath}/pair/${pair.id}/session`}
							className="text-purplish-blue text-center ml-2"
							target="_blank"
						>
							<i className="fa-solid fa-arrow-up-right-from-square" />
						</Link>
					</p>
				</div>
			</li>
			<li>
				<div className="flex flex-row gap-2 mt-2 align-items-center">
					<NumberCircle number={4} />
					<p>
						<Trans
							t={t}
							i18nKey="userPanel.welcomeDialog.pairContent.fourthElement"
							components={[
								<Link
									to={{ pathname: `${panelScopePath}/knowledge-base/directory/3` }}
									target="_blank"
								/>,
								<Link
									to={{ pathname: `${panelScopePath}/knowledge-base/article/60` }}
									target="_blank"
								/>,
							]}
						/>
						<Link
							to={`${panelScopePath}/pair/${pair.id}/session`}
							className="text-purplish-blue text-center ml-2"
							target="_blank"
						>
							<i className="fa-solid fa-arrow-up-right-from-square" />
						</Link>
					</p>
				</div>
			</li>
			<li>
				<div className="flex flex-row gap-2 mt-2 align-items-center">
					<p>
						<Trans
							t={t}
							i18nKey="userPanel.welcomeDialog.pairContent.fifthElement"
							components={[
								<Link
									to={{ pathname: `${panelScopePath}/knowledge-base/directory/3` }}
									target="_blank"
								/>,
							]}
						/>
					</p>
				</div>
			</li>
		</>
	);
};

export const WelcomeDialog = (props: DialogBaseExtendProps) => {
	const { t } = useTranslation();
	const { panelScopePath } = usePanel();
	const { userPairs, userPairsFetching } = usePanel();
	const { currentUser, loading } = useAuth();

	const sessionKey = `welcomePopupDisabled_${currentUser?.id}`;
	const isPopupDisabledInSession = sessionStorage.getItem(sessionKey) === 'true';

	const firstPair = userPairs?.length || 0 ? userPairs?.[0] : null;
	const showWelcomePopup = Boolean(currentUser?.welcomePopup) && !isPopupDisabledInSession && firstPair;

	const [visible, setVisible] = useState(false);

	const { mutate: disableWelcomePopup } = useMutation({
		mutationFn: () => usersApi.diableWelcomePopup(),
		onSuccess: () => {
			sessionStorage.setItem(sessionKey, 'true');
		},
	});

	useEffect(() => {
		if (!loading && showWelcomePopup) {
			setTimeout(() => {
				disableWelcomePopup();
				setVisible(true);
			}, 1000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, showWelcomePopup]);

	return (
		<Dialog
			{...props}
			size="lg"
			title={t('userPanel.welcomeDialog.title')}
			visible={visible}
			onHide={() => {
				setVisible(false);
			}}
			bodyClassName="grid"
		>
			<div className="col-12 lg:col-9">
				<p>
					<Trans t={t} i18nKey="userPanel.welcomeDialog.primaryTextWithPair" target="_blank" />
				</p>
				<div className="flex flex-row">
					<div className="flex-1">
						<span>
							<Trans
								t={t}
								i18nKey="userPanel.welcomeDialog.beforeList"
								components={[
									<Link
										to={{
											pathname: `${panelScopePath}/knowledge-base/article/60`,
										}}
										target="_blank"
									/>,
								]}
							/>
						</span>
						{userPairsFetching ? (
							<Spinner center />
						) : (
							<ul className="list-none">{firstPair && <WelcomeDialogPairContent pair={firstPair} />}</ul>
						)}
					</div>
				</div>
			</div>
			<div className="hidden lg:flex col-3 align-items-center">
				<img src={Gratz} alt="persons" role="none" style={{ maxWidth: 200 }} />
			</div>
		</Dialog>
	);
};
